import { AppointmentActivity } from "appointment-experience-client";
import { careNotesPathFor } from "../pages/care-note/careNoteLinks";
import { appointmentDetailsPath } from "./Appointment";

export const AppointmentActivityType = {
  CARE_NOTE: "care-note",
  ASSESSMENT: "assessment",
  TASK: "task",
  /** An appointment booked for the patient during the visit: the follow-up. */
  APPOINTMENT: "appointment",
} as const;

export type AppointmentActivityType =
  (typeof AppointmentActivityType)[keyof typeof AppointmentActivityType];

export const AppointmentActivityAction = {
  STARTED: "started",
  UPDATED: "updated",
  COMPLETED: "completed",
} as const;

export type AppointmentActivityAction =
  (typeof AppointmentActivityAction)[keyof typeof AppointmentActivityAction];

export interface AppointmentActivityItem {
  patientId: string;
  refId: string;
  metadata: Record<string, unknown> | null;
}

export interface ActivityTypeConfig {
  /** Section heading in the panel. */
  label: string;
  /** Material Symbols icon name. */
  icon: string;
  /** Short uppercase tag shown against the item in the appointment history. */
  tag: string;
  /** Badge text per status, where the default wording doesn't fit. */
  statusLabels?: Partial<Record<AppointmentActivityAction, string>>;
  /** In-app route to the item; omitted when the item can't be linked to. */
  buildLink?: (item: AppointmentActivityItem) => string | null;
}

export const DEFAULT_STATUS_LABELS: Record<AppointmentActivityAction, string> = {
  started: "Started",
  updated: "Updated",
  completed: "Completed",
};

export const ACTIVITY_TYPE_CONFIG: Record<string, ActivityTypeConfig> = {
  [AppointmentActivityType.CARE_NOTE]: {
    label: "Care Notes",
    icon: "description",
    tag: "NOTE",
    buildLink: ({ patientId, refId }) =>
      `${careNotesPathFor(patientId)}/${refId}`,
  },
  [AppointmentActivityType.ASSESSMENT]: {
    label: "Assessments",
    icon: "assignment",
    tag: "ASSESSMENT",
    // Assessments are routed by their clinical form id.
    buildLink: ({ patientId, refId, metadata }) =>
      typeof metadata?.formId === "string"
        ? `/patients/${patientId}/assessments/${metadata.formId}/resume/${refId}`
        : null,
  },
  [AppointmentActivityType.TASK]: {
    label: "Tasks",
    icon: "task_alt",
    tag: "TASK",
    statusLabels: { started: "Created" },
  },
  [AppointmentActivityType.APPOINTMENT]: {
    label: "Follow-up Appointments",
    icon: "event",
    tag: "FOLLOW-UP",
    statusLabels: { completed: "Booked" },
    buildLink: ({ patientId, refId }) => appointmentDetailsPath(refId, patientId),
  },
};

/** Used for a category the web doesn't know yet, so new API types still render. */
export const FALLBACK_ACTIVITY_TYPE_CONFIG: ActivityTypeConfig = {
  label: "Other",
  icon: "check_circle",
  tag: "ACTIVITY",
};

export const activityTypeConfig = (activityType: string): ActivityTypeConfig =>
  ACTIVITY_TYPE_CONFIG[activityType] ?? FALLBACK_ACTIVITY_TYPE_CONFIG;

export interface VisitActivity {
  activityType: string;
  refId: string;
  status: string;
  /** The clinical form a care note or assessment was made from. */
  formId: string | null;
  completedAt: string | null;
}

export const toVisitActivity = (activity: AppointmentActivity): VisitActivity => ({
  activityType: activity.activityType,
  refId: activity.refId,
  status: activity.status,
  formId:
    typeof activity.metadata?.formId === "string" ? activity.metadata.formId : null,
  completedAt: activity.completedAt ? new Date(activity.completedAt).toISOString() : null,
});

export const isVisitActivity = (value: unknown): value is VisitActivity => {
  if (typeof value !== "object" || value === null) return false;
  const activity = value as Partial<VisitActivity>;
  return (
    typeof activity.activityType === "string" &&
    typeof activity.refId === "string" &&
    typeof activity.status === "string" &&
    (activity.formId === null || typeof activity.formId === "string") &&
    (activity.completedAt === null || typeof activity.completedAt === "string")
  );
};
